<template>
  <div class="fill-split-button" :class="{ 'is-disabled': disabled || !templates.length, 'is-small': isSmall }">
    <!-- Main action: fill with the currently-selected (default) template. -->
    <button type="button" class="fill-split-button__main" :disabled="disabled || !templates.length" @click="onFill">
      <BaseIcon name="ai" class="fill-split-button__icon" />
      {{ label }}
    </button>
    <span class="fill-split-button__divider" />
    <!-- Chevron: pick a different template to fill with; on phones, in a sheet (docs/page-layout.md). -->
    <v-menu offset-y :disabled="isPhone || disabled || !templates.length" transition="scale-transition" min-width="260">
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="fill-split-button__chevron"
          :disabled="disabled || !templates.length"
          v-on="on"
          v-bind="attrs"
          @click="openSheet"
        >
          <BaseIcon name="chevron-down" class="fill-split-button__chevron-icon" />
        </button>
      </template>

      <div class="fill-split-button__menu">
        <div class="fill-split-button__menu-heading">{{ $t('page.opportunity-forms.fill-with') }}</div>
        <button
          v-for="template in templates"
          :key="template.id"
          type="button"
          class="fill-split-button__item"
          :class="{ 'is-active': template.id === defaultTemplateId }"
          @click.stop="onSelect(template)"
        >
          <span class="fill-split-button__item-name">{{ template.name }}</span>
          <span class="fill-split-button__item-meta">
            <BaseTag v-if="template.id === defaultTemplateId" preset="soft" tone="success">
              {{ $t('page.opportunity-forms.default') }}
            </BaseTag>
          </span>
        </button>
      </div>
    </v-menu>
    <BaseBottomSheet v-if="isPhone" v-model="sheetOpen" :title="$t('page.opportunity-forms.fill-with')">
      <ul class="base-bottom-sheet__menu">
        <li v-for="template in templates" :key="template.id">
          <button type="button" @click="onSheetSelect(template)">
            {{ template.name }}
            <BaseTag v-if="template.id === defaultTemplateId" preset="soft" tone="success">
              {{ $t('page.opportunity-forms.default') }}
            </BaseTag>
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag.vue';
import { viewport } from '@/util/viewport';

// Split button for the "Remplir" action. The main part fills with the
// currently-selected (default) gabarit; the chevron opens a menu to pick a
// different one. Emits `fill(templateId)`.
export default {
  name: 'FillSplitButton',
  components: { BaseBottomSheet, BaseIcon, BaseTag },
  props: {
    label: { type: String, required: true },
    templates: { type: Array, default: () => [] },
    defaultTemplateId: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // Compact size for inline/table use; defaults to the standard BaseButton size.
    isSmall: { type: Boolean, default: false },
  },
  data() {
    return { sheetOpen: false };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
  },
  methods: {
    openSheet() {
      if (this.isPhone) this.sheetOpen = true;
    },
    // As in every sheet, a choice closes it first.
    onSheetSelect(template) {
      this.sheetOpen = false;
      this.onSelect(template);
    },
    onFill() {
      this.$emit('fill', this.defaultTemplateId);
    },
    onSelect(template) {
      this.$emit('fill', template.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.fill-split-button {
  display: inline-flex;
  align-items: stretch;
  // Match the standard BaseButton height so header actions align.
  min-height: 44px;
  border-radius: $radius-full;
  background-color: $color-semantic-ai;
  color: $color-white;
  user-select: none;
  overflow: hidden;

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  &__main {
    display: inline-flex;
    align-items: center;
    gap: $space-2;
    padding: 8px 16px;
    font-size: 16px;
    white-space: nowrap;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: background-color $duration-fast $ease-ui-interaction;

    &:hover {
      background-color: rgba(0, 0, 0, 0.14);
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
  }

  // Compact variant for inline/table rows (matches BaseButton is-small).
  &.is-small {
    min-height: 28px;

    .fill-split-button__main {
      gap: $space-1;
      padding: $space-1 $space-3;
      @include text-style('label-md');
    }

    .fill-split-button__icon {
      width: 14px;
      height: 14px;
    }
  }

  &__divider {
    width: 1px;
    align-self: stretch;
    background: rgba(255, 255, 255, 0.35);
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    // Extra right padding compensates for the round border so the icon sits
    // visually centred in the rounded cap.
    padding: 0 $space-2 0 $space-1;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: background-color $duration-fast $ease-ui-interaction;

    &:hover {
      background-color: rgba(0, 0, 0, 0.14);
    }
  }

  &__chevron-icon {
    width: 16px;
    height: 16px;
  }

  &__menu {
    background: $color-surface-raised;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    padding: $space-1;
    box-shadow: $shadow-dropdown;
  }

  &__menu-heading {
    @include text-style('label-md');
    color: $color-text-muted;
    text-transform: uppercase;
    padding: $space-1 $space-2;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-md;
    width: 100%;
    padding: $space-2;
    background: transparent;
    border: none;
    border-radius: $radius-sm;
    cursor: pointer;
    text-align: left;
    transition: background-color $duration-fast $ease-ui-interaction;

    &:hover {
      background: $color-surface-sunken;
    }

    &.is-active {
      background: $color-highlight-pale;
    }
  }

  &__item-name {
    @include text-style('body-md');
    color: $color-text-title;
  }

  &__item-meta {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    flex-shrink: 0;
  }
}
</style>
